import React, { ReactNode, useContext, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import {
  JournalNote,
  useJournalFilterContext,
  useJournalNotesQuery,
} from 'features/journal';

type JournalContextType = {
  notes: JournalNote[];
  isLoading: boolean;
  noNotes: boolean;
  scrollRef: any;
  isFetchingNextPage: boolean;
};

const JournalContext = React.createContext<JournalContextType>({
  notes: [],
  isLoading: false,
  noNotes: true,
  scrollRef: null,
  isFetchingNextPage: false,
});

type JournalProviderProps = { children: ReactNode };

export const JournalProvider = ({ children }: JournalProviderProps) => {
  const { queryParams } = useJournalFilterContext();
  const { ref: scrollRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
      }
    },
  });
  const {
    data: notes = [],
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useJournalNotesQuery(queryParams);

  // @ts-ignore
  const noNotes = !notes || notes.length === 0;

  const providerValue = useMemo(
    () => ({
      notes,
      isLoading,
      noNotes,
      isFetchingNextPage,
      scrollRef,
    }),
    [notes, isLoading, noNotes, scrollRef, isFetchingNextPage]
  );

  return (
    // @ts-ignore
    <JournalContext.Provider value={providerValue}>
      {children}
    </JournalContext.Provider>
  );
};

export const useJournalContext = () => {
  const useJournalContext = useContext(JournalContext);

  if (!useJournalContext) {
    throw new Error(
      'useJournalContext must be used in JournalContext.Provider'
    );
  }

  return useJournalContext;
};
